import { pipeline, env } from '@huggingface/transformers';

// Configure transformers.js to always download models
env.allowLocalModels = false;
env.useBrowserCache = true;

export interface BackgroundRemovalResult {
  processedFile: File;
  processedUrl: string;
  originalSize: number;
  processedSize: number;
}

interface SegmentationResult {
  label: string;
  score: number | null;
  mask: {
    data: Float32Array | number[];
    width: number;
    height: number;
  };
}

const MAX_IMAGE_DIMENSION = 1024;

function resizeImageIfNeeded(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, image: HTMLImageElement) {
  let width = image.naturalWidth;
  let height = image.naturalHeight;

  if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
    if (width > height) {
      height = Math.round((height * MAX_IMAGE_DIMENSION) / width);
      width = MAX_IMAGE_DIMENSION;
    } else {
      width = Math.round((width * MAX_IMAGE_DIMENSION) / height);
      height = MAX_IMAGE_DIMENSION;
    }

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(image, 0, 0, width, height);
    return true;
  }

  canvas.width = width;
  canvas.height = height;
  ctx.drawImage(image, 0, 0);
  return false;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let segmenterInstance: any = null;

export const removeBackgroundFromImage = async (
  imageElement: HTMLImageElement,
  onProgress?: (stage: string) => void
): Promise<Blob> => {
  try {
    console.log('Starting background removal with @huggingface/transformers...');

    onProgress?.('Cargando modelo de IA...');
    
    // Reuse the pipeline instance if available
    if (!segmenterInstance) {
      segmenterInstance = await pipeline(
        'image-segmentation',
        'Xenova/segformer-b0-finetuned-ade-512-512',
        { device: 'webgpu' }
      );
    }
    
    onProgress?.('Procesando imagen...');
    
    // Convert HTMLImageElement to canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) throw new Error('Could not get canvas context');
    
    // Resize image if needed and draw it to canvas
    const wasResized = resizeImageIfNeeded(canvas, ctx, imageElement);
    console.log(`Image ${wasResized ? 'was' : 'was not'} resized. Final dimensions: ${canvas.width}x${canvas.height}`);
    
    // Get image data as base64
    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    console.log('Image converted to base64');
    
    // Process the image with the segmentation model
    console.log('Processing with segmentation model...');
    const rawResult = await segmenterInstance(imageData);
    const result = rawResult as SegmentationResult[];
    
    console.log('Segmentation result:', result);
    
    if (!result || !Array.isArray(result) || result.length === 0 || !result[0].mask) {
      throw new Error('Invalid segmentation result');
    }

    onProgress?.('Aplicando máscara de transparencia...');
    
    // Create a new canvas for the masked image
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = canvas.width;
    outputCanvas.height = canvas.height;
    const outputCtx = outputCanvas.getContext('2d');
    
    if (!outputCtx) throw new Error('Could not get output canvas context');
    
    // Draw original image
    outputCtx.drawImage(canvas, 0, 0);
    
    // Apply the mask
    const outputImageData = outputCtx.getImageData(
      0, 0,
      outputCanvas.width,
      outputCanvas.height
    );
    const data = outputImageData.data;
    const maskData = result[0].mask.data;
    
    // Apply inverted mask to alpha channel with gamma correction and thresholds
    const GAMMA = 1.3;
    const MIN_ALPHA = 10;
    const MAX_ALPHA = 245;

    for (let i = 0; i < maskData.length; i++) {
      const maskValue = maskData[i] as number;
      const inverted = 1 - maskValue;
      let alpha = Math.pow(inverted, GAMMA) * 255;

      if (alpha < MIN_ALPHA) alpha = 0;
      else if (alpha > MAX_ALPHA) alpha = 255;

      data[i * 4 + 3] = Math.round(alpha);
    }
    
    outputCtx.putImageData(outputImageData, 0, 0);
    console.log('Mask applied successfully');

    onProgress?.('Finalizando...');
    
    // Convert canvas to blob
    return new Promise((resolve, reject) => {
      outputCanvas.toBlob(
        (blob) => {
          if (blob) {
            console.log('Successfully created final blob');
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob'));
          }
        },
        'image/png',
        1.0
      );
    });
  } catch (error) {
    console.error('Error removing background:', error);
    throw error;
  }
};

export const loadImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

export const removeBackgroundFromFile = async (
  file: File,
  onProgress?: (stage: string) => void
): Promise<BackgroundRemovalResult> => {
  try {
    onProgress?.('Preparando imagen...');

    const imageElement = await loadImage(file);
    const processedBlob = await removeBackgroundFromImage(imageElement, onProgress);

    // Create a new File from the blob with PNG extension
    const fileName = file.name.replace(/\.[^/.]+$/, '') + '_sin_fondo.png';
    const processedFile = new File([processedBlob], fileName, { type: 'image/png' });
    const processedUrl = URL.createObjectURL(processedFile);

    return {
      processedFile,
      processedUrl,
      originalSize: file.size,
      processedSize: processedFile.size,
    };
  } catch (error) {
    console.error('Error in removeBackgroundFromFile:', error);
    throw error;
  }
};
